import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import { isValidEmail } from '../utils/emailValidation';
import './auth/LoginDesign.css';

const ForgotPassword = () => {
  const location = useLocation();
  
  // Pre-fill email from URL parameter if present (BUG-056 fix)
  const initialEmail = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('email') || '';
  }, [location.search]);
  
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');

    if (!email || !isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.forgotPassword(email);
      const message =
        response.data?.data?.message ||
        response.data?.message ||
        'If a matching account exists, a password-reset link has been sent.';
      setStatus(message);
    } catch (err) {
      // Neutral error to avoid account enumeration.
      setStatus('If a matching account exists, a password-reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <header className="login-header">
        <div className="login-header-inner">
          <Link to="/" className="login-logo">
            Studio<span className="login-logo-accent">AEye</span>
          </Link>
          <Link to="/login" className="login-back-link">
            ← Back to login
          </Link>
        </div>
      </header>

      <main className="login-main">
        <div className="login-card">
          <div className="login-heading">
            <h1 className="login-title">Reset your password</h1>
            <p className="login-subtitle">
              Enter the email on your StudioAEye account and we'll send you a link to set a new
              password. Prefer a passwordless option?{' '}
              <Link to="/login" className="login-link">
                Use a magic link instead
              </Link>
              .
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="email" className="login-label">
                Your email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-input"
                disabled={loading}
              />
            </div>

            {error && (
              <div role="alert" className="login-alert login-alert-error">
                {error}
              </div>
            )}

            {status && <div className="login-alert login-alert-success">{status}</div>}

            <button type="submit" disabled={loading} className="login-submit">
              {loading ? (
                <>
                  <span className="login-spinner" />
                  Sending reset link…
                </>
              ) : (
                'Send reset link'
              )}
            </button>

            <div className="login-forgot">
              <Link to="/login">Remembered your password? Log in</Link>
            </div>
          </form>

          <p className="login-footer">© StudioAEye {new Date().getFullYear()}</p>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
